import { getConfig } from '@/config';
import {
  InteractionRequiredAuthError,
  PublicClientApplication,
  type AccountInfo,
  type Configuration,
} from '@azure/msal-browser';

const config = getConfig();

const msalConfig: Configuration = {
  auth: {
    clientId: config.SHAREPOINT_CLIENT_ID,
    authority: `https://login.microsoftonline.com/${config.SHAREPOINT_TENANT_ID}`,
    redirectUri: window.location.origin,
  },
  cache: {
    cacheLocation: 'localStorage',
    storeAuthStateInCookie: false,
  },
};

const graphRequest = {
  scopes: ['https://graph.microsoft.com/Sites.Selected'],
};

/**
 * Separate MSAL instance/app registration from entra-auth-service.ts's CESG login — file
 * CRUD and the sites pass-through both need a delegated Sites.Selected Graph token the app's
 * own login never requests. In dev this is genuinely a different Entra tenant than the main
 * app (see SHAREPOINT_INTEGRATION_PLAN.md's "why not OBO" section); in prod it happens to be
 * the same tenant/app, but this code never branches on which case it's in.
 *
 * Same instantiation shape as sharepoint-picker-auth.ts (CESG-785, unmerged) — silent→popup
 * fallback mirroring entra-auth-service.ts. Scopes/usage differ: that service never calls
 * Graph itself, this one does.
 */
class SharePointAuthService {
  private msalInstance: PublicClientApplication;
  private initialized = false;

  constructor(config: Configuration) {
    this.msalInstance = new PublicClientApplication(config);
  }

  private async ensureInitialized(): Promise<void> {
    if (!this.initialized) {
      await this.msalInstance.initialize();
      this.initialized = true;
    }
  }

  private async ensureAccount(): Promise<AccountInfo> {
    /*
     * The active-account pointer, never `getAllAccounts()[0]` — see the fuller reasoning in
     * entra-auth-service.ts. msal-browser namespaces token cache keys by clientId but NOT account keys,
     * so this instance and the app-login one read one shared per-origin account list; index 0 there
     * would hand the CESG-tenant account to a Sites.Selected request against the SharePoint tenant.
     * This pointer is clientId-scoped (`msal.<clientId>.active-account-filters`), so it can only ever
     * name the account THIS registration signed in.
     *
     * A `{ realm: SHAREPOINT_TENANT_ID }` filter would be wrong for the same reason it is wrong there:
     * `realm` is the account's HOME tenant, not the authority it signed into, so a guest account matches
     * nothing and this would pop a login on every single call.
     *
     * When the pointer is absent this logs in once and records it, which is also the migration path for a
     * session established before this pointer was being trusted.
     */
    const existing = this.msalInstance.getActiveAccount();
    if (existing) {
      return existing;
    }

    const authResult = await this.msalInstance.loginPopup(graphRequest);
    this.msalInstance.setActiveAccount(authResult.account);
    return authResult.account;
  }

  /**
   * Acquires a delegated Graph access token for arbitrary scopes against THIS tenant, interactively via
   * popup if silent acquisition fails (no cached session, consent needed, etc.).
   *
   * Scope-parameterised rather than hardcoding Sites.Selected, mirroring
   * entraAuthService.acquireTokenOrThrow. The login in ensureAccount above still requests only
   * Sites.Selected, so a scope beyond that is consented incrementally — the first request for it raises
   * InteractionRequiredAuthError and the popup below collects consent once.
   *
   * The only caller passing anything else today is the dev-only mail-token override
   * (services/graph/mail-token.ts). Sites.Selected remains the sole scope this service needs in
   * production.
   */
  public async acquireToken(scopes: string[]): Promise<string> {
    await this.ensureInitialized();
    const account = await this.ensureAccount();
    const tokenRequest = { scopes, account };

    try {
      const response = await this.msalInstance.acquireTokenSilent(tokenRequest);
      return response.accessToken;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        const response = await this.msalInstance.acquireTokenPopup(tokenRequest);
        return response.accessToken;
      }
      throw error;
    }
  }

  /** Silent-only: `acquireToken` falls back to `loginPopup`, which is unusable during render. */
  public async acquireTokenSilentOrNull(
    scopes: string[],
  ): Promise<string | null> {
    await this.ensureInitialized();
    const account = this.msalInstance.getActiveAccount();
    if (!account) return null;

    try {
      const response = await this.msalInstance.acquireTokenSilent({
        scopes,
        account,
      });
      return response.accessToken;
    } catch {
      return null;
    }
  }

  /** The delegated Sites.Selected token every drive operation uses. */
  public acquireGraphToken(): Promise<string> {
    return this.acquireToken(graphRequest.scopes);
  }
}

export const sharePointAuthService = new SharePointAuthService(msalConfig);
